import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { eventTypeLabels, eventTypes, type EventType as EventTypeName } from '@greasemap/validation';
import { LocationBar } from '../../events/LocationBar';
import { useMapCentre } from '../../events/useMapCentre';
import { useEventSearch } from '../../events/queries';
import type { EventSearchParams, EventType } from '../../events/types';
import { EventsMap } from '../../events/map/EventsMap';
import type { MapMarker } from '../../events/map/MapProvider';
import { EventCard } from './EventCard';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Heading,
  Select,
  Spinner,
  Stack,
  Text,
  TextField,
} from '../../ui';

const RADIUS_OPTIONS = [
  { value: '25', label: 'Within 25 km' },
  { value: '50', label: 'Within 50 km' },
  { value: '100', label: 'Within 100 km' },
  { value: '250', label: 'Within 250 km' },
];

/** Converts a `yyyy-mm-dd` field to an inclusive local-day instant, or undefined when blank. */
function dayBoundary(date: string, endOfDay: boolean): string | undefined {
  if (date === '') {
    return undefined;
  }

  const time = endOfDay ? 'T23:59:59' : 'T00:00:00';
  const parsed = new Date(`${date}${time}`);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
}

/**
 * Discovery: a map of nearby events, the same results as a list, and the filters that drive both.
 *
 * The map and the list read the one search, so they never disagree. When the map cannot load the
 * list still works; when the search is empty the screen says so and offers to widen it rather than
 * showing a blank map that reads as broken.
 *
 * @returns The discovery screen.
 */
export function DiscoverPage(): React.JSX.Element {
  const navigate = useNavigate();

  const mapCentre = useMapCentre();
  const centre = mapCentre.centre;
  const [radiusKm, setRadiusKm] = useState(50);
  const [types, setTypes] = useState<readonly EventType[]>([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const params: EventSearchParams = useMemo(
    () => ({
      lat: centre.lat,
      lon: centre.lon,
      radiusKm,
      from: dayBoundary(from, false),
      to: dayBoundary(to, true),
      types: types.length > 0 ? types : undefined,
    }),
    [centre, radiusKm, from, to, types],
  );

  const { data, isPending, isError, error, refetch } = useEventSearch(params);

  const markers: MapMarker[] = useMemo(
    () => (data ?? []).map((event) => ({ id: event.id, latitude: event.latitude, longitude: event.longitude })),
    [data],
  );

  function toggleType(type: EventType): void {
    setTypes((current) =>
      current.includes(type) ? current.filter((value) => value !== type) : [...current, type],
    );
  }

  return (
    <Stack gap="xl">
      <Stack direction="row" gap="md" align="center" justify="space-between" wrap>
        <Heading level={1}>Discover</Heading>
        <Stack direction="row" gap="sm" wrap>
          <Link className="gm-button gm-button--secondary" to="/me/rsvps">
            My RSVPs
          </Link>
          <Link className="gm-button gm-button--primary" to="/events/new">
            Submit an event
          </Link>
        </Stack>
      </Stack>

      <LocationBar state={mapCentre} />

      <Card ariaLabel="Filters">
        <Stack gap="lg">
          <Stack direction="row" gap="lg" wrap>
            <Select
              label="Distance"
              options={RADIUS_OPTIONS}
              value={String(radiusKm)}
              onChange={(event) => setRadiusKm(Number(event.target.value))}
            />
            <TextField
              label="From"
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
            />
            <TextField
              label="To"
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
            />
          </Stack>

          <Stack gap="xs">
            <Text size="sm" muted>
              Event types
            </Text>
            <Stack direction="row" gap="xs" wrap>
              {eventTypes.map((type) => (
                <Button
                  key={type}
                  variant={types.includes(type) ? 'primary' : 'secondary'}
                  onClick={() => toggleType(type)}
                  aria-pressed={types.includes(type)}
                >
                  {eventTypeLabels[type as EventTypeName]}
                </Button>
              ))}
            </Stack>
          </Stack>
        </Stack>
      </Card>

      <div className="gm-discover">
        <EventsMap
          markers={markers}
          view={{ latitude: centre.lat, longitude: centre.lon }}
          onMarkerClick={(id) => void navigate(`/events/${id}`)}
        />

        <div>
          {isPending && (
            <Stack gap="md" align="center">
              <Spinner label="Loading events" />
              <Text muted>Finding events near you…</Text>
            </Stack>
          )}

          {isError && <ErrorState message={error.message} onRetry={() => void refetch()} />}

          {!isPending && !isError && data.length === 0 && (
            <EmptyState
              title="No events in this area for these dates"
              description="Widen the distance or clear the date filter to see more."
              action={
                <Button variant="primary" onClick={() => setRadiusKm(250)}>
                  Widen the search
                </Button>
              }
            />
          )}

          {!isPending && !isError && data.length > 0 && (
            <Stack gap="sm">
              <Badge tone="neutral">{data.length} events</Badge>
              <div className="gm-event-list">
                {data.map((event) => (
                  <EventCard key={event.id} event={event} />
                ))}
              </div>
            </Stack>
          )}
        </div>
      </div>
    </Stack>
  );
}
